import type {
  ActivePhase,
  DeliverableEntry,
  DeliverableStatus,
  Step,
  Subject,
  CompleteState,
} from '@/lib/dd-queue/types'
import {
  CLOSEOUT_KEYS,
  DELIVERABLE_PHASE_KEYS,
  STEP_LIBRARY,
  STEPS_BY_KEY,
} from '@/lib/dd-queue/data/steps'
import { ddQueueCurrentOperator } from '@/lib/dd-queue/ddQueueIdentity'

export function stepByKey(key: string): Step | undefined {
  return STEPS_BY_KEY[key]
}

export function getActivePhase(deliverables: Record<string, DeliverableEntry>): ActivePhase {
  const done = (k: string) =>
    deliverables[k]?.status === 'complete' || deliverables[k]?.status === 'not_required'

  if (!done('pm_assessment')) return 'intake'
  if (!done('design') || !done('estimation')) return 'design_est'
  if (!done('sales_approval')) return 'approval'
  if (!done('sku') || !done('uin')) return 'system'
  if (DELIVERABLE_PHASE_KEYS.some((k) => !done(k))) return 'deliverables'
  if (CLOSEOUT_KEYS.some((k) => !done(k))) return 'closeout'
  return 'complete'
}

export function resolveOpenDeliverables(
  deliverables: Record<string, DeliverableEntry>,
  subject: Pick<Subject, 'estimationMode'>
): Record<string, DeliverableEntry> {
  const phase = getActivePhase(deliverables)
  const d = { ...deliverables }

  const promote = (key: string) => {
    if (d[key]?.status === 'pending') {
      d[key] = { ...d[key], status: 'open' }
    }
  }

  switch (phase) {
    case 'intake':
      promote('pm_assessment')
      break
    case 'design_est':
      if (subject.estimationMode === 'estimation_first') {
        promote('estimation')
        if (d.estimation?.status === 'complete') promote('design')
      } else {
        promote('design')
        if (subject.estimationMode === 'standard' || d.design?.status === 'complete') {
          promote('estimation')
        }
      }
      break
    case 'approval':
      promote('sales_approval')
      break
    case 'system':
      promote('sku')
      promote('uin')
      break
    case 'deliverables':
      for (const k of DELIVERABLE_PHASE_KEYS) promote(k)
      break
    case 'closeout': {
      const done = (k: string) =>
        d[k]?.status === 'complete' || d[k]?.status === 'not_required'
      promote('product_review')
      if (done('product_review')) promote('pim_publish')
      // DD-GENERATE-ORDER-003 — terminal is piq_quickpart + web_publish (both done → phase complete).
      if (done('pim_publish')) {
        promote('piq_quickpart')
        promote('web_publish')
      }
      break
    }
  }

  return d
}

export function getOpenDeliverables(deliverables: Record<string, DeliverableEntry>): string[] {
  return Object.entries(deliverables)
    .filter(([, entry]) => entry.status === 'open')
    .map(([k]) => k)
}

export function hasOpenDeliverableForRole(
  deliverables: Record<string, DeliverableEntry>,
  role: string
): boolean {
  return Object.entries(deliverables).some(
    ([key, entry]) => entry.status === 'open' && stepByKey(key)?.role === role
  )
}

export function hasOpenDeliverableForUser(
  deliverables: Record<string, DeliverableEntry>,
  user: string
): boolean {
  return Object.values(deliverables).some(
    (entry) => entry.status === 'open' && entry.assignee === user
  )
}

export function hasOpenDeliverableForRoleAndUser(
  deliverables: Record<string, DeliverableEntry>,
  role: string,
  user: string
): boolean {
  return Object.entries(deliverables).some(
    ([key, entry]) =>
      entry.status === 'open' && entry.assignee === user && stepByKey(key)?.role === role
  )
}

export function getDeliverableCTAState(
  deliverables: Record<string, DeliverableEntry>,
  stepKey: string,
  isSupervisor = false
): CompleteState {
  const entry = deliverables[stepKey]
  const step = stepByKey(stepKey)

  if (!entry || !step) {
    return { action: 'none', enabled: false, label: 'Unknown step', reason: '', ctaLabel: '' }
  }

  const status = entry.status

  if (status === 'not_required') {
    return {
      action: 'none',
      enabled: false,
      label: 'Not required',
      reason: 'This step is not required for this product.',
      ctaLabel: 'Not required',
    }
  }
  if (status === 'pending') {
    return {
      action: 'none',
      enabled: false,
      label: 'Not yet actionable',
      reason: 'Previous phases must complete first.',
      ctaLabel: 'Waiting',
    }
  }
  if (status === 'complete') {
    return {
      action: 'none',
      enabled: false,
      label: `${step.name} complete`,
      reason: 'Already completed.',
      ctaLabel: 'Complete',
    }
  }

  if (step.type === 'auto') {
    return {
      action: 'none',
      enabled: false,
      label: `Auto: ${step.name}`,
      reason: 'This step runs automatically.',
      ctaLabel: 'Auto in progress',
    }
  }
  if (step.external) {
    if (stepKey === 'sales_approval') {
      return {
        action: 'none',
        enabled: false,
        label: 'Sales Review',
        reason: 'Sales reviews the Proof Packet and approves (or requests changes) in Origin.',
        ctaLabel: 'In Sales Review',
      }
    }
    return {
      action: 'none',
      enabled: false,
      label: `External: ${step.name}`,
      reason: 'Handled by an external team.',
      ctaLabel: 'External',
    }
  }
  if (step.unassignable) {
    return {
      action: 'complete',
      enabled: true,
      label: `Complete: ${step.name}`,
      reason: `Anyone can complete ${step.name}.`,
      ctaLabel: 'Mark complete',
    }
  }
  if (!entry.assignee) {
    return {
      action: 'assign',
      enabled: true,
      label: 'Assign',
      reason: `No one is assigned to ${step.name}.`,
      ctaLabel: 'Assign',
      stepRole: step.role,
    }
  }
  if (entry.assignee === 'Client') {
    return {
      action: 'none',
      enabled: false,
      label: 'Awaiting client',
      reason: "Waiting on the client's review.",
      ctaLabel: 'Awaiting client',
    }
  }
  if (entry.assignee !== ddQueueCurrentOperator().name) {
    const first = entry.assignee.split(' ')[0]
    if (isSupervisor) {
      return {
        action: 'complete',
        enabled: true,
        label: `Complete: ${step.name}`,
        reason: `Override — complete on behalf of ${entry.assignee}.`,
        ctaLabel: `Complete (for ${first})`,
      }
    }
    return {
      action: 'none',
      enabled: false,
      label: `Assigned to ${first}`,
      reason: 'Only the assignee can complete this step.',
      ctaLabel: `Assigned to ${entry.assignee}`,
    }
  }

  return {
    action: 'complete',
    enabled: true,
    label: `Complete: ${step.name}`,
    reason: `Mark "${step.name}" as done.`,
    ctaLabel: 'Mark complete',
  }
}

export function advanceDeliverable(
  deliverables: Record<string, DeliverableEntry>,
  stepKey: string,
  subject: Pick<Subject, 'estimationMode'>
): Record<string, DeliverableEntry> {
  const d = {
    ...deliverables,
    [stepKey]: { ...deliverables[stepKey], status: 'complete' as DeliverableStatus },
  }
  return resolveOpenDeliverables(d, subject)
}

/** Reference workflow.ts — auto-complete open auto steps when phase opens. */
export function autoCompleteOpen(
  deliverables: Record<string, DeliverableEntry>,
  estimationMode: Subject['estimationMode'] = 'standard'
): Record<string, DeliverableEntry> {
  const d = { ...deliverables }
  let changed = true
  while (changed) {
    changed = false
    for (const [key, entry] of Object.entries(d)) {
      const step = stepByKey(key)
      if (entry.status === 'open' && step?.type === 'auto') {
        d[key] = { status: 'complete', assignee: 'RUKH System' }
        changed = true
      }
    }
    if (changed) {
      Object.assign(d, resolveOpenDeliverables(d, { estimationMode }))
    }
  }
  return d
}

export function phaseLabel(phase: ActivePhase): string {
  const labels: Record<ActivePhase, string> = {
    intake: 'Intake',
    design_est: 'Design & Estimation',
    approval: 'Sales Review',
    system: 'System',
    deliverables: 'Deliverables',
    closeout: 'Close-out',
    complete: 'Complete',
  }
  return labels[phase]
}

/** Close-out dependency chain — mirrors resolveOpenDeliverables closeout case. */
const CLOSEOUT_DOWNSTREAM: Record<string, string[]> = {
  product_review: ['pim_publish', 'piq_quickpart', 'web_publish'],
  pim_publish: ['piq_quickpart', 'web_publish'],
  piq_quickpart: [],
  web_publish: [],
}

const POST_DESIGN_EST_KEYS = [
  'sales_approval',
  'sku',
  'uin',
  ...DELIVERABLE_PHASE_KEYS,
  ...CLOSEOUT_KEYS,
]

/** Steps that must be redone when a completed step is reactivated for rework. */
export function getDownstreamDependentSteps(
  stepKey: string,
  estimationMode: Subject['estimationMode']
): string[] {
  if (CLOSEOUT_DOWNSTREAM[stepKey]) {
    return CLOSEOUT_DOWNSTREAM[stepKey]
  }
  if (DELIVERABLE_PHASE_KEYS.includes(stepKey)) {
    return [...CLOSEOUT_KEYS]
  }
  if (stepKey === 'sku' || stepKey === 'uin') {
    return [...DELIVERABLE_PHASE_KEYS, ...CLOSEOUT_KEYS]
  }
  if (stepKey === 'sales_approval') {
    return POST_DESIGN_EST_KEYS.filter((k) => k !== 'sales_approval')
  }
  if (stepKey === 'design') {
    const downstream = [...POST_DESIGN_EST_KEYS]
    if (estimationMode === 'design_first') {
      return ['estimation', ...downstream]
    }
    return downstream
  }
  if (stepKey === 'estimation') {
    const downstream = [...POST_DESIGN_EST_KEYS]
    if (estimationMode === 'estimation_first') {
      return ['design', ...downstream]
    }
    return downstream
  }
  if (stepKey === 'pm_assessment') {
    return STEP_LIBRARY.map((s) => s.key).filter((k) => k !== 'pm_assessment')
  }
  return []
}

export interface StepReactivationPlan {
  deliverableUpdates: Record<string, Partial<DeliverableEntry>>
  clearCompletedAt: string[]
  clearOpenedAt: string[]
  clearQuotedProofId: boolean
}

/** Pure reactivation plan: reopen stepKey and invalidate downstream dependents. */
export function buildStepReactivation(
  deliverables: Record<string, DeliverableEntry>,
  stepKey: string,
  estimationMode: Subject['estimationMode']
): StepReactivationPlan {
  const downstream = getDownstreamDependentSteps(stepKey, estimationMode)
  const reactivatedAssignee = deliverables[stepKey]?.assignee ?? null

  const deliverableUpdates: Record<string, Partial<DeliverableEntry>> = {
    [stepKey]: { status: 'open', assignee: reactivatedAssignee },
  }

  for (const key of downstream) {
    const entry = deliverables[key]
    if (!entry || entry.status === 'not_required') continue
    if (entry.status === 'complete' || entry.status === 'open') {
      deliverableUpdates[key] = { status: 'pending', assignee: entry.assignee }
    }
  }

  const invalidatedDesign =
    stepKey === 'design' || downstream.includes('design')

  return {
    deliverableUpdates,
    clearCompletedAt: [stepKey, ...downstream],
    clearOpenedAt: downstream,
    clearQuotedProofId: invalidatedDesign,
  }
}
